import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBook, FaCalendarAlt, FaCreditCard, FaPlus, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

const ArrayFieldContainer = styled.div`
  margin-bottom: 20px;
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.04'});
  border: ${props => props.darkMode ? '1px solid #444' : 'none'};
`;

const ArrayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ArrayLabel = styled.label`
  font-weight: 600;
  color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  }
`;

const ArrayItemContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  background: ${props => props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.darkMode ? '#444' : '#e9ecef'};
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const ArrayItemTextarea = styled.textarea`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.darkMode ? '#555' : '#e2e8f0'};
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 38px;
  line-height: 1.5;
  background: ${props => props.darkMode ? '#333' : 'white'};
  color: ${props => props.darkMode ? '#f5f5f5' : 'inherit'};

  &:focus {
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    outline: none;
  }

  &::placeholder {
    color: ${props => props.darkMode ? '#aaa' : '#a0aec0'};
  }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  color: ${props => props.danger ? '#e74c3c' : props.color || '#3498db'};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: ${props => props.danger ? '#fee2e2' : props.hoverBg || '#e2f5fe'};
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const PolicyChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: ${props => props.bg || '#e2f5fe'};
  color: ${props => props.color || '#3498db'};
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border-left: 3px solid ${props => props.borderColor || '#3498db'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Add a handler for preventing form submission on textarea Enter
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
  }
};

const renderArrayField = (
  field, 
  label, 
  placeholder,
  bgColor,
  borderColor, 
  iconComponent, 
  tempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode,
  currentInputValues,
  setCurrentInputValues
) => {
  // Function to add item and clear input
  const handleAddAndClear = () => {
    const inputValue = currentInputValues[field]?.trim();
    if (inputValue) {
      console.log("Adding policy to", field, ":", inputValue);
      
      // Add the current input value to the array
      handleAddArrayItem(field, inputValue);
      
      // Clear the input field
      setCurrentInputValues({
        ...currentInputValues,
        [field]: ''
      });
      
      console.log("tempArrayFields after adding policy:", tempArrayFields[field]);
    }
  };

  // Handle input change
  const handleInputChange = (value) => {
    setCurrentInputValues({
      ...currentInputValues,
      [field]: value
    });
  };

  // Handle key press to add on Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddAndClear();
    }
  };

  return (
    <ArrayFieldContainer darkMode={darkMode}>
      <ArrayHeader>
        <ArrayLabel darkMode={darkMode}>
          {iconComponent} {label}
        </ArrayLabel>
      </ArrayHeader>
      
      {/* Single input field for adding new items */}
      <div className="mb-3">
        <div className="d-flex">
          <ArrayItemTextarea
            value={currentInputValues[field] || ''}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            rows={currentInputValues[field]?.length > 50 ? 3 : 1}
            darkMode={darkMode}
            onKeyDown={handleKeyPress}
            className="flex-grow-1"
          />
          <button
            type="button"
            className="btn btn-primary ms-2 align-self-start"
            onClick={handleAddAndClear}
            disabled={!currentInputValues[field]?.trim()}
          >
            <FaPlus size={14} /> Add
          </button>
        </div>
      </div>
      
      {/* Empty or no items message */}
      {(!tempArrayFields[field] || tempArrayFields[field].length === 0) && (
        <div className={`text-muted py-2 px-3 mb-2 ${darkMode ? 'bg-dark' : 'bg-light'} rounded`}>
          No {label.toLowerCase()} added yet.
        </div>
      )}
      
      {/* Preview section with chips */}
      {tempArrayFields[field]?.length > 0 && (
        <div className="mt-3">
          <h6 className={`${darkMode ? 'text-light' : 'text-muted'} mb-2 small`}>Preview:</h6>
          <ChipsContainer>
            {tempArrayFields[field].map((item, index) => (
              item && (
                <PolicyChip
                  key={index} 
                  bg={darkMode ? bgColor === '#fff8e6' ? '#423204' : 
                      bgColor === '#f0e6ff' ? '#2a1758' : 
                      bgColor === '#e2f5fe' ? '#103a52' : '#282828' : bgColor} 
                  color={darkMode ? '#f5f5f5' : 'inherit'}
                  borderColor={borderColor}
                  onClick={() => handleRemoveArrayItem(field, index)}
                  title="Click to remove"
                >
                  {item} <FaTrash size={12} className="ms-2" />
                </PolicyChip>
              )
            ))}
          </ChipsContainer>
        </div>
      )}
    </ArrayFieldContainer>
  );
};

const PoliciesTab = ({ 
  formData, 
  handleChange,
  tempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode
}) => {
  // Add state for current input values
  const [currentInputValues, setCurrentInputValues] = useState({
    houseRules: '',
    cancellationPolicy: '',
    paymentPolicy: ''
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {renderArrayField(
        'houseRules', 
        'House Rules', 
        'e.g. Check-in: 2:00 PM, No smoking',
        '#fff8e6',
        darkMode ? '#ffc107' : '#f39c12',
        <FaBook />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
      
      {renderArrayField(
        'cancellationPolicy', 
        'Cancellation Policy', 
        'e.g. Free cancellation up to 30 days before arrival',
        '#f0e6ff',
        darkMode ? '#a78bfa' : '#6f42c1',
        <FaCalendarAlt />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
      
      {renderArrayField(
        'paymentPolicy', 
        'Payment Policy', 
        'e.g. 50% deposit required at booking',
        '#e2f5fe',
        darkMode ? '#63b3ed' : '#3498db',
        <FaCreditCard />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
    </motion.div>
  );
};

export default PoliciesTab;
